
import React from 'react';
import { FilterOptions } from '@/components/FilterModal';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { getAllIndustries } from '@/data'; // Portfolio import will be changed
import { Portfolio } from '@/schemas/portfolio'; // Changed to Zod-inferred type
import { MultiSelect } from '@/components/business/leads/filters/MultiSelect';
import { useFirebase } from '@/context/FirebaseProvider'; // Changed from useDataUtils

interface InlineFilterSectionProps {
  filters: FilterOptions;
  onFilterChange: (filters: FilterOptions) => void;
  onReset: () => void;
  portfolioSearch: string;
  setPortfolioSearch: (search: string) => void;
  industrySearch: string;
  setIndustrySearch: (search: string) => void;
  filteredPortfolios: Portfolio[]; // This prop might need to align with Zod Portfolio type if used elsewhere
}

const InlineFilterSection: React.FC<InlineFilterSectionProps> = ({
  filters,
  onFilterChange,
  onReset,
  portfolioSearch,
  setPortfolioSearch,
  industrySearch,
  setIndustrySearch,
  filteredPortfolios // This prop's type might need to be Portfolio[] from Zod if it's derived from FirebaseProvider data
}) => {
  const isMobile = useIsMobile();
  const { portfolios: portfoliosFromFirebase } = useFirebase(); // Use useFirebase
  const availableIndustries = getAllIndustries();
  
  const portfolioNames = (portfoliosFromFirebase || [])
    .map(portfolio => portfolio.name || 'Unnamed Portfolio') // Portfolio Zod schema has 'name'
    .filter(Boolean);
  
  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'onboarding', label: 'Onboarding' }
  ];
  
  const portfolioOptions = portfolioNames.map(name => ({ // Changed portfolioTitles to portfolioNames, iterator to name
    value: name,
    label: name // Changed portfolioName to name
  }));
  
  const industryOptions = availableIndustries.map(industry => ({
    value: industry,
    label: industry
  }));
  
  const handleStatusChange = (selected: string[]) => {
    onFilterChange({
      ...filters,
      status: selected
    });
  };
  
  const handlePortfolioChange = (selected: string[]) => {
    onFilterChange({
      ...filters,
      portfolios: selected
    });
  };
  
  const handleIndustryChange = (selected: string[]) => {
    onFilterChange({
      ...filters,
      industries: selected
    });
  };

  return (
    <div className="w-full bg-white rounded-lg shadow-sm p-4 mb-2 animate-in slide-in-from-top-5 duration-300">
      <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : 'md:grid-cols-4 gap-6'} mb-2`}>
        <div key="status" className="flex flex-col space-y-1.5">
          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Status</label>
          <MultiSelect
            id="status-filter"
            options={statusOptions}
            selectedValues={filters.status}
            onChange={handleStatusChange}
            placeholder="Select"
          />
        </div>
        
        <div key="portfolio" className="flex flex-col space-y-1.5">
          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Portfolio</label>
          <MultiSelect
            id="portfolio-filter"
            options={portfolioOptions}
            selectedValues={filters.portfolios}
            onChange={handlePortfolioChange}
            placeholder="Select"
          />
        </div>
        
        <div key="industry" className="flex flex-col space-y-1.5">
          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Industry</label>
          <MultiSelect
            id="industry-filter"
            options={industryOptions}
            selectedValues={filters.industries}
            onChange={handleIndustryChange}
            placeholder="Select"
          />
        </div>

        <div className="flex flex-col justify-end">
          <Button variant="outline" onClick={onReset} className="w-full">
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InlineFilterSection;
